
import { useState } from 'react';

interface Booking {
  id: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  booking_date: string;
  booking_time: string;
  status: string;
  payment_status: string;
  payment_id: string;
  message: string;
  created_at: string;
  currency: string;
  amount_paid: number;
}

interface BookingsTableProps {
  bookings: Booking[];
  onUpdateStatus: (id: string, status: string) => void;
  onDelete: (id: string) => void;
}

export default function BookingsTable({ bookings, onUpdateStatus, onDelete }: BookingsTableProps) {
  const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'cancelled'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const filtered = bookings.filter((b) => {
    const matchesFilter = filter === 'all' || b.status === filter;
    const matchesSearch =
      b.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.client_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.client_phone.includes(searchQuery);
    return matchesFilter && matchesSearch;
  });

  const formatDate = (date: string) =>
    new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${minutes} ${ampm}`;
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      cancelled: 'bg-red-50 text-red-700 border-red-200',
    };
    return styles[status] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const paymentBadge = (status: string) => {
    const styles: Record<string, string> = {
      paid: 'bg-emerald-50 text-emerald-700',
      pending: 'bg-amber-50 text-amber-700',
      failed: 'bg-red-50 text-red-700',
    };
    return styles[status] || 'bg-gray-50 text-gray-700';
  };

  const filters: { label: string; value: 'all' | 'pending' | 'confirmed' | 'cancelled' }[] = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Confirmed', value: 'confirmed' },
    { label: 'Cancelled', value: 'cancelled' },
  ];

  const handleDelete = (id: string) => {
    onDelete(id);
    setDeleteConfirm(null);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="p-4 sm:p-5 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base sm:text-lg font-bold text-slate-900">Bookings</h3>
          <span className="text-xs text-gray-400 font-medium">{filtered.length} results</span>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <div className="flex bg-gray-100 rounded-full p-1 overflow-x-auto flex-shrink-0">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-3 sm:px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                  filter === f.value
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-gray-500 hover:text-slate-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="py-16 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-calendar-line text-2xl text-gray-400"></i>
          </div>
          <p className="text-sm text-gray-500 font-medium">No bookings found</p>
          <p className="text-xs text-gray-400 mt-1">Bookings will appear here once clients book consultations</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/80">
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Client</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Date &amp; Time</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Payment</th>
                  <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Status</th>
                  <th className="text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((booking) => (
                  <>
                  <tr
                    key={booking.id}
                    className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer"
                    onClick={() => setExpandedRow(expandedRow === booking.id ? null : booking.id)}
                  >
                    <td className="px-5 py-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{booking.client_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{booking.client_email}</p>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-sm text-slate-900 font-medium">{formatDate(booking.booking_date)}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{formatTime(booking.booking_time)}</p>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-sm font-semibold text-slate-900">
                        {booking.currency} {Number(booking.amount_paid).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                      <span className={`inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${paymentBadge(booking.payment_status)}`}>
                        {booking.payment_status}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full border ${statusBadge(booking.status)}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {booking.status === 'pending' && (
                          <>
                            <button
                              onClick={() => onUpdateStatus(booking.id, 'confirmed')}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors cursor-pointer"
                              title="Confirm"
                            >
                              <i className="ri-check-line text-base"></i>
                            </button>
                            <button
                              onClick={() => onUpdateStatus(booking.id, 'cancelled')}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer"
                              title="Cancel"
                            >
                              <i className="ri-close-line text-base"></i>
                            </button>
                          </>
                        )}
                        {booking.status === 'confirmed' && (
                          <button
                            onClick={() => onUpdateStatus(booking.id, 'cancelled')}
                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors cursor-pointer"
                            title="Cancel"
                          >
                            <i className="ri-close-line text-base"></i>
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteConfirm(booking.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-6-line text-base"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedRow === booking.id && (
                    <tr key={`${booking.id}-details`} className="bg-slate-50/70">
                      <td colSpan={5} className="px-5 py-4">
                        <div className="bg-white rounded-xl border border-gray-100 p-5">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Client Details</p>
                          <div className="grid grid-cols-3 gap-5">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 shrink-0">
                                <i className="ri-user-line text-sm"></i>
                              </div>
                              <div>
                                <p className="text-[11px] text-gray-400 font-medium">Full Name</p>
                                <p className="text-sm text-slate-900 font-medium mt-0.5">{booking.client_name}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 shrink-0">
                                <i className="ri-mail-line text-sm"></i>
                              </div>
                              <div>
                                <p className="text-[11px] text-gray-400 font-medium">Email Address</p>
                                <p className="text-sm text-slate-900 font-medium mt-0.5">{booking.client_email}</p>
                              </div>
                            </div>
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 shrink-0">
                                <i className="ri-phone-line text-sm"></i>
                              </div>
                              <div>
                                <p className="text-[11px] text-gray-400 font-medium">Phone Number</p>
                                <p className="text-sm text-slate-900 font-medium mt-0.5">{booking.client_phone || 'Not provided'}</p>
                              </div>
                            </div>
                          </div>
                          {booking.message && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 shrink-0">
                                  <i className="ri-message-3-line text-sm"></i>
                                </div>
                                <div>
                                  <p className="text-[11px] text-gray-400 font-medium">Message</p>
                                  <p className="text-sm text-slate-700 mt-0.5 leading-relaxed">{booking.message}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          {booking.payment_id && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <div className="flex items-start gap-3">
                                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 shrink-0">
                                  <i className="ri-secure-payment-line text-sm"></i>
                                </div>
                                <div>
                                  <p className="text-[11px] text-gray-400 font-medium">Payment ID</p>
                                  <p className="text-sm text-slate-900 font-mono mt-0.5">{booking.payment_id}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <p className="text-[11px] text-gray-400">Booked on {new Date(booking.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden divide-y divide-gray-100">
            {filtered.map((booking) => (
              <div key={booking.id} className="p-4">
                <div
                  className="flex items-start justify-between cursor-pointer"
                  onClick={() => setExpandedRow(expandedRow === booking.id ? null : booking.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{booking.client_name}</p>
                      <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${statusBadge(booking.status)}`}>
                        {booking.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{formatDate(booking.booking_date)} at {formatTime(booking.booking_time)}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <p className="text-xs font-semibold text-slate-700">
                        {booking.currency} {Number(booking.amount_paid).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${paymentBadge(booking.payment_status)}`}>
                        {booking.payment_status}
                      </span>
                    </div>
                  </div>
                  <i className={`ri-arrow-${expandedRow === booking.id ? 'up' : 'down'}-s-line text-gray-400 text-lg flex-shrink-0 ml-2`}></i>
                </div>

                {expandedRow === booking.id && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <i className="ri-mail-line text-xs text-emerald-500"></i>
                        <p className="text-xs text-gray-600">{booking.client_email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-phone-line text-xs text-emerald-500"></i>
                        <p className="text-xs text-gray-600">{booking.client_phone || 'Not provided'}</p>
                      </div>
                      {booking.message && (
                        <div className="flex items-start gap-2">
                          <i className="ri-message-3-line text-xs text-emerald-500 mt-0.5"></i>
                          <p className="text-xs text-gray-600 leading-relaxed">{booking.message}</p>
                        </div>
                      )}
                      {booking.payment_id && (
                        <div className="flex items-center gap-2">
                          <i className="ri-secure-payment-line text-xs text-emerald-500"></i>
                          <p className="text-xs text-gray-600 font-mono truncate">{booking.payment_id}</p>
                        </div>
                      )}
                      <p className="text-[11px] text-gray-400">Booked on {new Date(booking.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                    <div className="flex items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                      {booking.status === 'pending' && (
                        <>
                          <button
                            onClick={() => onUpdateStatus(booking.id, 'confirmed')}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-50 text-emerald-600 text-xs font-medium hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-check-line text-sm"></i>
                            Confirm
                          </button>
                          <button
                            onClick={() => onUpdateStatus(booking.id, 'cancelled')}
                            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-close-line text-sm"></i>
                            Cancel
                          </button>
                        </>
                      )}
                      {booking.status === 'confirmed' && (
                        <button
                          onClick={() => onUpdateStatus(booking.id, 'cancelled')}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-close-line text-sm"></i>
                          Cancel
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteConfirm(booking.id)}
                        className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                      >
                        <i className="ri-delete-bin-6-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 animate-[slideUp_0.2s_ease-out]">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-6-line text-xl text-red-500"></i>
            </div>
            <h3 className="text-base font-bold text-slate-900 text-center">Delete Booking</h3>
            <p className="text-sm text-gray-500 text-center mt-2">
              Are you sure you want to delete this booking? This action cannot be undone.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-sm font-medium text-white hover:bg-red-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
